import { useQuery } from "@apollo/client";
import { Button } from "@samdesk/components/Button";
import { Form } from "@samdesk/components/Form";
import { Modal } from "@samdesk/components/Modal";
import { showPrompt } from "@samdesk/components/Prompt";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import { ReactElement, useState } from "react";
import CenteredCircularProgress from "ui-library/CenteredCircularProgress";
import { DebugInfo } from "ui-library/DebugInfo";
import useSnackbar from "utils/useSnackbar";
import { GenericIntegrationInput } from "./GenericIntegrationInput";
import { GetIntegrationResult, Integration } from "./Integration";

interface GenericIntegrationModalProps {
	integration: Integration;
	open: boolean;
	onClose: () => void;
	integrationGID?: GID;
}

export function GenericIntegrationModal({
	integration,
	open,
	onClose,
	integrationGID,
}: GenericIntegrationModalProps): ReactElement {
	const { handleSuccess, handleError } = useSnackbar();

	const [isDirty, setIsDirty] = useState(false);
	const [isValid, setIsValid] = useState(false);

	const isCreate = !integrationGID;
	const { data, loading } = useQuery(integration.actions.get, {
		variables: { gid: integrationGID as GID }, // TODO: how do we avoid casting here?
		skip: isCreate || !open,
	});

	const defaultValues = getDefaultValues(integration.fields, data);

	const onRequestClose = async () => {
		if (isDirty) {
			const { state } = await showPrompt({
				promptType: "confirm",
				title: "Discard changes to this integration?",
				description:
					"You have unsaved changes to your integration. Your changes will not be saved.",
				confirmButton: {
					label: "Discard Changes",
				},
				cancelButton: {
					label: "Keep Editing",
				},
			});

			if (state === "ok") {
				onClose();
			}
		} else {
			onClose();
		}
	};

	return (
		<Modal
			title={
				isCreate ?
					`Create ${integration.name} Integration`
				:	`Update ${integration.name} Integration`
			}
			open={open}
			onRequestClose={onRequestClose}
		>
			{loading ?
				<CenteredCircularProgress />
			:	<Form
					onSubmit={async (formData) => {
						if (isCreate) {
							const result = await integration.actions.create(formData);

							if (result.error) {
								handleError(result.error.message);
							} else if (result.success) {
								handleSuccess(`Created new integration: ${formData.name}`);
							}
						} else {
							const result = await integration.actions.update(
								integrationGID,
								formData
							);

							if (result.error) {
								handleError(result.error.message);
							} else if (result.success) {
								handleSuccess(`Successfully updated ${formData.name}`);
							}
						}

						onClose();
					}}
					defaultValues={defaultValues}
					onStateChange={({ isValid, isDirty }) => {
						setIsValid(isValid);
						setIsDirty(isDirty);
					}}
				>
					<Stack>
						{integration.fields.map((field) => (
							<GenericIntegrationInput
								field={field}
								integrationGID={integrationGID}
								key={field.name}
							/>
						))}
						<DebugInfo>
							<Text prominence="tertiary">
								Form is valid: {isValid ? "Yes" : "No"}
								<br />
								Form is dirty: {isDirty ? "Yes" : "No"}
							</Text>
						</DebugInfo>
						<Stack direction="row" spacing="small" justifyContent="end">
							<Button
								prominence="secondary"
								label="Cancel"
								onClick={onRequestClose}
							/>
							<Button
								submit
								label={isCreate ? `Create` : `Update`}
								prominence="primary"
							/>
						</Stack>
					</Stack>
				</Form>
			}
		</Modal>
	);
}

function getDefaultValues(
	fields: Integration["fields"],
	data?: GetIntegrationResult
) {
	const defaultValues: { [key: string]: string | boolean } = {};

	fields.forEach((field) => {
		defaultValues[field.name] = data?.integration?.[field.name] || "";
	});

	return defaultValues;
}
